import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, RotateCcw } from 'lucide-react';
import { Clip } from '../types';
import { formatDuration } from '../services/api';
import Tooltip from './Tooltip';

interface CustomVideoPlayerProps {
  clip?: Clip;
  src?: string;
  className?: string;
}

const CustomVideoPlayer: React.FC<CustomVideoPlayerProps> = ({ clip, src, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get the source URL
  const videoUrl = src || (clip ? (clip.file_url || clip.metadata?.source_video_url) : '');
  
  // Determine if this is a direct clip file (not a stream from source video)
  const isDirectClipFile = clip && clip.file_url && !clip.file_url.includes('/api/clips/stream/');
  
  // For clip mode, calculate the current clip time
  const currentClipTime = clip ? 
    (isDirectClipFile ? currentTime : Math.max(0, Math.min(currentTime - clip.start_time, clip.duration))) : 
    currentTime;
  const displayDuration = clip ? clip.duration : duration;
  
  // Ensure progress doesn't exceed 100% for clips
  const safeCurrentClipTime = Math.min(currentClipTime, displayDuration);
  
  // Initialize video when component mounts
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      
      // For clips, seek to clip start when video is ready (only for source video clips, not direct clip files)
      if (clip && !isDirectClipFile) {
        video.currentTime = clip.start_time;
        setCurrentTime(clip.start_time);
      } else {
        setCurrentTime(0);
      }
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      if (!isDragging) {
        const time = video.currentTime;
        setCurrentTime(time);

        // For clips from source video, auto-pause and loop at end of clip (not needed for direct clip files)
        if (clip && !isDirectClipFile && time >= clip.end_time) {
          video.pause();
          video.currentTime = clip.start_time;
          setIsPlaying(false);
          setCurrentTime(clip.start_time);
        }
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = (_e: any) => {
      setError('Failed to load video');
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip, isDragging]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      // For clips from source video, ensure we're in the clip range before playing (not needed for direct clip files)
      if (clip && !isDirectClipFile && (video.currentTime < clip.start_time || video.currentTime > clip.end_time)) {
        video.currentTime = clip.start_time;
      }
      
      video.play().then(() => {
        // Play succeeded
      }).catch(_error => {
        setError('Failed to play video');
      });
    } else {
      video.pause();
    }
  };

  const handleSeek = (clickX: number, progressWidth: number) => {
    const video = videoRef.current;
    if (!video) return;

    const percent = Math.max(0, Math.min(clickX / progressWidth, 1));
    
    if (clip && !isDirectClipFile) {
      // For clips from source video, seek within the clip range
      const clipTime = percent * clip.duration;
      const absoluteTime = clip.start_time + clipTime;
      video.currentTime = absoluteTime;
      setCurrentTime(absoluteTime);
    } else {
      // For full videos or direct clip files, seek within the entire duration
      const time = percent * (clip ? clip.duration : duration);
      video.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    handleSeek(clickX, rect.width);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    
    if (!progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    handleSeek(clickX, rect.width);
  };

  // Global mouse handlers for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !progressRef.current) return;
      
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      handleSeek(clickX, rect.width);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, clip, duration]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.volume = volume;
      setIsMuted(false);
    } else {
      video.volume = 0;
      setIsMuted(true);
    }
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    }
  };

  const handleRestart = () => {
    const video = videoRef.current;
    if (!video) return;

    if (clip && !isDirectClipFile) {
      video.currentTime = clip.start_time;
      setCurrentTime(clip.start_time);
    } else {
      video.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const progressPercent = displayDuration > 0 ? (safeCurrentClipTime / displayDuration) * 100 : 0;

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      {/* Video Element */}
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full"
        preload="metadata"
        crossOrigin="anonymous"
        playsInline
        onClick={handlePlayPause}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-white text-center">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p>Loading video...</p>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="text-white text-center p-4">
            <p className="text-red-400 mb-2">⚠️ {error}</p>
            <button 
              onClick={() => {
                setError(null);
                setIsLoading(true);
                const video = videoRef.current;
                if (video) {
                  video.load();
                }
              }}
              className="btn-primary text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && !isLoading && !error && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={handlePlayPause}
        >
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors">
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Custom Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/60 to-transparent p-4">
        <div className="flex items-center space-x-4 text-white">
          {/* Play/Pause Button */}
          <button
            onClick={handlePlayPause}
            className="flex items-center justify-center w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </button>

          {/* Restart Button */}
          <Tooltip
            content={clip ? "Restart clip" : "Restart video"}
            position="top"
          >
            <button
              onClick={handleRestart}
              className="flex items-center justify-center w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </Tooltip>

          {/* Time Display */}
          <div className="text-sm font-medium min-w-0">
            {formatDuration(safeCurrentClipTime)} / {formatDuration(displayDuration)}
          </div>

          {/* Progress Bar */}
          <div className="flex-1 relative">
            <div
              ref={progressRef}
              className="h-2 bg-white/20 rounded-full cursor-pointer relative"
              onClick={handleProgressClick}
              onMouseDown={handleProgressMouseDown}
            >
              <div
                className="h-full bg-red-500 rounded-full relative"
                style={{ width: `${progressPercent}%` }}
              >
                <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-lg" />
              </div>
            </div>
          </div>

          {/* Volume Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleMuteToggle}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
            
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-20 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer volume-slider"
              style={{
                background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) 100%)`
              }}
            />
          </div>

          {/* Fullscreen Button */}
          <button
            onClick={handleFullscreen}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <Maximize className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer; 